import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'theSource/app/routes';
import { Loader, Spacer } from 'theSource/components';
import {
  CommunicationPreferencesContainerStyled,
  ExtraInfoStyled,
  EmailInputContainerStyled,
  EmptyEmailContainerStyled,
  PreferredLanguageContainerStyled,
} from 'enrollment/features';
import {
  GoPaperless,
  AllowPushNotification,
  AllowTextMessages,
  getFormValues,
  EditEmailAddresses,
} from '..';
import 'enrollment/features/CommunicationPreferences/components/CommunicationPreferencesForm/CommunicationPreferencesFormStyles.css';
import {
  ACCEPTED_VALUES,
  getLocalLanguage,
} from 'theSource/features/Login/utils';
import { mapOptions, useApiMutation } from 'theSource/utils';
import {
  CompanyManifestResponseDto,
  GET_COMPANY_MANIFEST_QUERY_KEY,
  useCompanySettingsQuery,
  useConfigurationQuery,
} from 'theSource/queries';
import {
  GET_SUBSCRIBER_COMMUNICATION_PREFERENCES_KEY,
  PUT_SUBSCRIBER_COMMUNICATION_PREFERENCES_QUERY_KEY,
  SubscriberCommunicationPreferencesDto,
  useSubscriberCommunicationPreferencesQuery,
} from 'enrollment/queries';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
// import noEmailAnimation from '../../assets/noEmailAnimation.gif';

export const CommunicationPreferencesForm = () => {
  const { t } = useTranslation('pages.communicationPreferencesLumos');
  const [editEmail, setEditEmail] = useState(false);
  const [addEmail, setAddEmail] = useState(false);
  const [addAltEmail, setAddAltEmail] = useState(false);
  const [prefLanguage, setprefLanguage] = useState(getLocalLanguage());
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const manifestData: CompanyManifestResponseDto = queryClient.getQueryData(
    GET_COMPANY_MANIFEST_QUERY_KEY
  )!;
  const { configuration, isLoadingConfiguration } = useConfigurationQuery();

  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });
  const lumosApiHost = configuration?.apiHost;

  const { data, isLoading, isFetching } =
    useSubscriberCommunicationPreferencesQuery({
      enabled: !isLoadingConfiguration,
      lumosApiHost: String(lumosApiHost),
    });

  const { mutate: SubscriberCommunicationPreferencesRequest } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SubscriberCommunicationPreferencesDto
  >({
    queryKey: PUT_SUBSCRIBER_COMMUNICATION_PREFERENCES_QUERY_KEY,
    queryKeysToInvalidate: GET_SUBSCRIBER_COMMUNICATION_PREFERENCES_KEY,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/employees/communication_preferences`,
    onSuccess: () => {
      navigate(ROUTES.main.home.path);
    },
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const handleSubmit = () => {
    const values = getFormValues();

    SubscriberCommunicationPreferencesRequest(values, {
      onError: (error) => {
        console.log(error);
      },
    });
  };

  const preferences = data?.data;
  const emailsPresent = preferences?.email_1 && preferences?.email_2;

  useEffect(() => {
    setAddEmail(Boolean(emailsPresent));
  }, [emailsPresent]);

  if (isLoadingConfiguration || isLoading || isFetching) {
    return <Loader />;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      id="communication-preferences-form"
      data-testid="communication-preferences-form"
    >
      <CommunicationPreferencesContainerStyled>
        <h2>{t('header')}</h2>

        {manifestData.enabled_languages &&
          manifestData.enabled_languages.length > 1 && (
            <PreferredLanguageContainerStyled>
              <EmailInputContainerStyled>
                <ps-select-field
                  label={t('language')}
                  name="language"
                  input-id="language-selector"
                  first-option={
                    prefLanguage !== null ? prefLanguage : undefined
                  }
                  onChange={(event) => {
                    if (ACCEPTED_VALUES.includes(event.currentTarget.value)) {
                      setprefLanguage(event.currentTarget.value);
                    }
                  }}
                >
                  {mapOptions(manifestData.enabled_languages).map((option) => {
                    return (
                      <option
                        key={option.key}
                        label={option.label}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    );
                  })}
                </ps-select-field>
              </EmailInputContainerStyled>
            </PreferredLanguageContainerStyled>
          )}

        <Spacer height={6} />
        <Typography variant="body1">{t('email')}</Typography>
        <Spacer height={1} />

        <ExtraInfoStyled className="body2">{t('emailInfo')}</ExtraInfoStyled>
        <Spacer height={6} />

        {addEmail ? (
          <EditEmailAddresses
            preferences={preferences}
            editEmail={editEmail}
            setEditEmail={setEditEmail}
            emailsPresent={emailsPresent}
            addEmail={addAltEmail}
            setAddEmail={setAddAltEmail}
          />
        ) : (
          <EmptyEmailContainerStyled>
            {/* <img src={noEmailAnimation} alt="missing emails" /> */}
            <ExtraInfoStyled className="body2">
              {t('emailEmpty')}
            </ExtraInfoStyled>
            <Spacer height={4} />
            <ps-button
              id="add_email"
              label="Add Email Address"
              level="tertiary"
              icon="plus"
              onClick={() => setAddEmail(!addEmail)}
              data-testid="add-email-button"
            ></ps-button>
          </EmptyEmailContainerStyled>
        )}

        <Spacer height={4} />

        <AllowTextMessages
          phoneNum={preferences?.phone_number}
          visible={true}
          checked={preferences?.allow_text}
        />
        <AllowPushNotification
          visible={
            companySettings && companySettings.data['enable_push_notification']
          }
          checked={preferences?.allow_push}
        />
      </CommunicationPreferencesContainerStyled>

      <Spacer height={6} />
      <CommunicationPreferencesContainerStyled>
        <GoPaperless checked={preferences?.paperless} />
      </CommunicationPreferencesContainerStyled>

      <Spacer height={6} />
      <ps-button
        id="submit"
        label={t('submitCommPref')}
        level="primary"
        onClick={() => handleSubmit()}
      ></ps-button>
    </Box>
  );
};
